import { useState } from "react";

import { PRODUCTION_MODEL } from "../../../services/claude";
import { PromptTemplate, PromptTemplateCreationParams } from "../../../types/prompt-template";
import usePromptTemplates from "../../explore/hooks/usePromptTemplates";

const defaultNewTemplate: PromptTemplateCreationParams = {
  title: "",
  description: "",
  prompt: "",
  model_config: {
    provider: "anthropic",
    model: PRODUCTION_MODEL.id,
    temperature: 0.3,
    max_tokens: 8000,
    input_variables: []
  },
  metadata: {
    category: "training",
    about: "individual",
    audience: "individual",
    primary_function: "onboarding",
    scope: []
  },
  enabled: false
};

interface LensLibraryState {
  isEditorOpen: boolean;
  currentTemplate: PromptTemplate | null;
  isConfirmingDelete: boolean;
  templateToDelete: PromptTemplate | null;
}

const initialState: LensLibraryState = {
  isEditorOpen: false,
  currentTemplate: null,
  isConfirmingDelete: false,
  templateToDelete: null
};

interface EditorState {
  isOpen: boolean;
  template: PromptTemplate | null;
  defaultTemplate: PromptTemplateCreationParams;
  close: () => void;
  refetch: () => void;
}

interface DeletionState {
  isConfirming: boolean;
  template: PromptTemplate | null;
  confirm: () => Promise<void>;
  cancel: () => void;
}

interface LensLibraryContext {
  templates: PromptTemplate[];
  loading: boolean;
  error: Error | null;
  editor: EditorState;
  deletion: DeletionState;
  onCreate: () => void;
  onEdit: (template: PromptTemplate) => void;
  onStatusChange: (template: PromptTemplate) => Promise<void>;
  onDeleteRequest: (template: PromptTemplate) => void;
}

const useLensLibrary = (): LensLibraryContext => {
  const {
    templates, loading, error, fetchTemplates, deleteTemplate, toggleTemplateStatus
  } = usePromptTemplates();
  const [state, setState] = useState<LensLibraryState>(initialState);

  const onCreate = (): void => {
    setState(prev => ({ ...prev, isEditorOpen: true, currentTemplate: null }));
  };

  const onEdit = (template: PromptTemplate): void => {
    setState(prev => ({ ...prev, isEditorOpen: true, currentTemplate: template }));
  };

  const closeEditor = (): void => {
    setState(prev => ({ ...prev, isEditorOpen: false, currentTemplate: null }));
  };

  const onStatusChange = async (template: PromptTemplate): Promise<void> => {
    await toggleTemplateStatus(template.id, !template.enabled);
  };

  const onDeleteRequest = (template: PromptTemplate): void => {
    setState(prev => ({ ...prev, isConfirmingDelete: true, templateToDelete: template }));
  };

  const confirmDelete = async (): Promise<void> => {
    if (!state.templateToDelete) {
      return;
    }
    await deleteTemplate(state.templateToDelete.id);
    setState(prev => ({ ...prev, isConfirmingDelete: false, templateToDelete: null }));
  };

  const cancelDelete = (): void => {
    setState(prev => ({ ...prev, isConfirmingDelete: false, templateToDelete: null }));
  };

  return {
    templates, loading, error,
    editor: {
      isOpen: state.isEditorOpen, template: state.currentTemplate,
      defaultTemplate: defaultNewTemplate, close: closeEditor, refetch: fetchTemplates
    },
    deletion: {
      isConfirming: state.isConfirmingDelete, template: state.templateToDelete,
      confirm: confirmDelete, cancel: cancelDelete
    },
    onCreate, onEdit, onStatusChange, onDeleteRequest
  };
};

export default useLensLibrary;
